import { PayloadAction } from '@reduxjs/toolkit';
import { NormalizedV2Response } from '@/types/eds/api/responses/NormalizedV2Response';
import { SelectOption } from '@/features/editMode/types/selectOption';
import { call, put, takeLatest } from 'redux-saga/effects';
import EdsAdapterService from '@/features/eds/services/EdsAdapterService/EdsAdapterService';
import {
  getCreateDocumentClaimants,
  getCreateDocumentClaimantsSuccess,
  getCreateDocumentClaims,
  getCreateDocumentClaimsSuccess,
  setCreateDocumentOptionsLoading,
} from '@/domains/notes/store/slices/createNoteDocumentSlice';
import HttpService from '@/services/HttpService/HttpService';

function* getCreateDocumentClaimsSaga({ payload }: PayloadAction<string>) {
  try {
    yield put(setCreateDocumentOptionsLoading(true));

    const { data } = yield call(
      HttpService.get,
      `/v2/ClaimSet/${payload}?includeEntities=ClaimEntities`,
      {},
      {
        baseURL: import.meta.env.VITE_API_URL_EDS,
      },
    );

    const normalizedResponse: NormalizedV2Response = yield call(EdsAdapterService.normalize, data);

    const claimOptions = (normalizedResponse.entities.ClaimEntities as NormalizedV2Response[]).reduce(
      (acc, claimEntity) => {
        if (claimEntity?.virtualFields?.isDeleted) return acc;

        return [
          ...acc,
          { label: claimEntity.fields.claimNumber?.value?.toString() || '', value: claimEntity.id.toString() },
        ];
      },
      [] as SelectOption[],
    );

    if (data) {
      yield put(getCreateDocumentClaimsSuccess(claimOptions));
    }
  } catch (error) {
    console.error(error);
  } finally {
    yield put(setCreateDocumentOptionsLoading(false));
  }
}

function* getCreateDocumentClaimantsSaga({ payload }: PayloadAction<string>) {
  try {
    yield put(setCreateDocumentOptionsLoading(true));

    const { data } = yield call(
      HttpService.get,
      `/v2/ClaimSet/${payload}?includeEntities=ClaimEntities.Claimants`,
      {},
      {
        baseURL: import.meta.env.VITE_API_URL_EDS,
      },
    );

    const normalizedResponse: NormalizedV2Response = yield call(EdsAdapterService.normalize, data);

    const claimantOptions = (normalizedResponse.entities.ClaimEntities as NormalizedV2Response[]).flatMap(
      (claim) =>
        (claim.entities.Claimants as NormalizedV2Response[])?.map((claimant) => ({
          label: `${claimant.fields.claimantName?.value} (${claim.fields.claimNumber?.value || 'unknown'})`,
          value: claimant.id.toString(),
        })) || [],
    );

    if (data) {
      yield put(getCreateDocumentClaimantsSuccess(claimantOptions));
    }
  } catch (error) {
    console.error(error);
  } finally {
    yield put(setCreateDocumentOptionsLoading(false));
  }
}

export function* createNoteDocumentWatcher() {
  yield takeLatest(getCreateDocumentClaims.type, getCreateDocumentClaimsSaga);
  yield takeLatest(getCreateDocumentClaimants.type, getCreateDocumentClaimantsSaga);
}
